import Fuse from "fuse.js";
import { createSelector } from "reselect";
import { EMPTY_ARRAY } from "lib";
import {
  SideLoadedBusinessRow,
  SideLoadedMatchedField,
  SideLoadedValidation,
  PIC_DELIMITER,
} from "constants/sideLoadedBusinesses";
import {
  getSideLoadedBusinessValidationIssues,
  isSideLoadedBusinessValid,
} from "lib/sideLoadedBusinesses/validation";
import { getSideLoadedBusinesses, getSideLoadedDataSets } from "./root";
import { selectSideLoadedDataSetLabelsById } from "./sideLoadedDataSets";
import { selectSideLoadedDataMappingEntriesByDataSetIdLookup } from "./sideLoadedDataMappings";
import {
  selectBusinessIdByAgentCodeLookup,
  selectBusinessIdBySaleyardCodeLookup,
} from "./indexes";

export const getSideLoadedBusinessById =
  (recordId: string) => (state: unknown) =>
    getSideLoadedBusinesses(state)[recordId] || null;

export const selectSideLoadedBusinessesList = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) =>
    Object.values(sideLoadedBusinesses),
);

export const selectSideLoadedBusinessesByDatasetId =
  (datasetId: string) =>
  (state: unknown): SideLoadedBusiness[] =>
    selectSideLoadedBusinessesList(state).filter(
      business => business.datasetId === datasetId,
    );

export interface SideLoadedValidationContext {
  businessIdByAgentCodeLookup: Record<string, string>;
  businessIdBySaleyardCodeLookup: Record<string, string>;
  mappingEntriesByDataSetId: Record<string, SideLoadedDataMappingEntry[]>;
}

export const selectSideLoadedValidationContext = createSelector(
  [
    selectBusinessIdByAgentCodeLookup as (
      state: State,
    ) => Record<string, string>,
    selectBusinessIdBySaleyardCodeLookup as (
      state: State,
    ) => Record<string, string>,
    selectSideLoadedDataMappingEntriesByDataSetIdLookup,
  ],
  (
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    mappingEntriesByDataSetId,
  ): SideLoadedValidationContext => ({
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    mappingEntriesByDataSetId,
  }),
);

interface CachedValidation {
  businessIdByAgentCodeLookup: Record<string, string>;
  businessIdBySaleyardCodeLookup: Record<string, string>;
  entries: SideLoadedDataMappingEntry[] | undefined;
  validation: SideLoadedValidation;
}

const validationCache = new WeakMap<SideLoadedBusiness, CachedValidation>();

const getCachedValidation = (
  record: SideLoadedBusiness,
  {
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    mappingEntriesByDataSetId,
  }: SideLoadedValidationContext,
): SideLoadedValidation => {
  const entries = mappingEntriesByDataSetId[record.datasetId];
  const cached = validationCache.get(record);
  if (
    cached &&
    cached.businessIdByAgentCodeLookup === businessIdByAgentCodeLookup &&
    cached.businessIdBySaleyardCodeLookup === businessIdBySaleyardCodeLookup &&
    cached.entries === entries
  ) {
    return cached.validation;
  }
  const options = {
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    entries,
  };
  const isValid = isSideLoadedBusinessValid(record, options);
  let issues: string[] | null = isValid ? [] : null;
  const validation: SideLoadedValidation = {
    isValid,
    getValidationIssues: () => {
      issues ??= getSideLoadedBusinessValidationIssues(record, options);
      return issues;
    },
  };
  validationCache.set(record, {
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    entries,
    validation,
  });
  return validation;
};

// Carried on the row rather than resolved in a valueGetter: the grid groups on
// this value, and a change to it has to reach the grid as a row data change.
export const selectSideLoadedBusinessesGridList = createSelector(
  [
    selectSideLoadedBusinessesList,
    selectSideLoadedDataSetLabelsById,
    selectSideLoadedValidationContext,
  ],
  (
    sideLoadedBusinesses: SideLoadedBusiness[],
    labelsById: Record<string, string>,
    validationContext: SideLoadedValidationContext,
  ): SideLoadedBusinessRow[] =>
    sideLoadedBusinesses.map(sideLoadedBusiness => ({
      id: sideLoadedBusiness.id,
      sideLoadedBusiness,
      // Falls back to the name until the data sets have loaded.
      datasetLabel:
        labelsById[sideLoadedBusiness.datasetId] ||
        sideLoadedBusiness.datasetName,
      // Validated when the grid reads the row, not up front.
      getValidation: () =>
        getCachedValidation(sideLoadedBusiness, validationContext),
    })),
);

export const getSideLoadedBusinessesCount = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) =>
    Object.values(sideLoadedBusinesses).length,
);

export interface SearchFormattedSideLoadedBusiness {
  sideLoadedBusiness: SideLoadedBusiness;
  formattedName: string;
}

// Fuse options for searching side loaded businesses.
// These should be pretty good for finding a sweet spot of results.
// One common idiom it needs to handle is finding "Kevin Turner"
// when searching for any of "Turner, K", "Turner Associates", "Kevin T", etc - by tokeninzing down to
// simplified text, we should be able to get some results for all of these, without too much noise.
// ref: https://www.fusejs.io/api/options.html
const FUSE_OPTIONS: Fuse.IFuseOptions<SearchFormattedSideLoadedBusiness> = {
  keys: [
    { name: "sideLoadedBusiness.name", weight: 2 },
    { name: "formattedName", weight: 3 },
    { name: "sideLoadedBusiness.abn", weight: 1 },
  ],
  includeScore: true,
  isCaseSensitive: false,
  minMatchCharLength: 2, // ignore single character matches
  shouldSort: true,
  useExtendedSearch: true, // for tokenizing the search query and joining by "|" (OR)
  findAllMatches: true, // better rank multi token matches
  threshold: 0.2,
  // for matching primarily on names, the location of where the match occurs has little
  // to do with the ranking of the match, so lets ignore it.
  // e.g.
  //   - "Kevin Turner & John Carter of Mars"
  //   - "John Carter of Mars & Kevin Turner"
  // both should be a good match for Kevin
  // https://www.fusejs.io/concepts/scoring-theory.html#distance-threshold-and-location
  ignoreLocation: true,
  // complicated by using tokenized extended search, because we break up the search query to tokens,
  // the standard Field-length Norm setting means that we would always favor the shorter indexes,
  // https://www.fusejs.io/concepts/scoring-theory.html#field-length-norm
  ignoreFieldNorm: true,
};

// Tokenize the search name field into a format that Fuse can easily search.
// - strip punctuation
// - convert whitespace to single space
function formatSearchNameField(name: string): string {
  return name
    .replace(/[^\w\s]/g, " ")
    .trim()
    .split(/\s+/)
    .join(" ");
}

const IGNORED_SEARCH_TOKENS = ["and", "co", "inc", "ltd", "pty", "the"];

// Tokenize the search query into a format that Fuse can understand - basically
// - strip punctuation
// - split on space
// - ignore single character tokens
// - use include match with short tokens
function formatSearchQueryTokens(query: string): string[] {
  return query
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(n => n.length >= 2)
    .filter(n => !IGNORED_SEARCH_TOKENS.includes(n.toLowerCase()))
    .map(term => (term.length <= 3 ? `'${term}` : term));
}

export const selectSideLoadedBusinessSearchIndex = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) => {
    const searchFormattedBusinesses: SearchFormattedSideLoadedBusiness[] =
      Object.values(sideLoadedBusinesses).map(sideLoadedBusiness => ({
        sideLoadedBusiness,
        formattedName: formatSearchNameField(sideLoadedBusiness.name),
      }));
    return new Fuse(searchFormattedBusinesses, FUSE_OPTIONS);
  },
);

export const searchSideLoadedBusinesses =
  (query: string, strict: boolean = false, includeIgnored: boolean = false) =>
  (state: State): Fuse.FuseResult<SearchFormattedSideLoadedBusiness>[] => {
    const formattedQueryTokens = formatSearchQueryTokens(query);
    if (!formattedQueryTokens.length) {
      return EMPTY_ARRAY;
    }

    const index = selectSideLoadedBusinessSearchIndex(state);

    const results = strict
      ? // strict search - get matches for all supplied tokens (AND = " ")
        index.search(formattedQueryTokens.join(" "))
      : // non-strict search - match any supplied tokens (OR = "|")
        index.search(formattedQueryTokens.join("|"));

    // Filtered on the results rather than the index so the one index still
    // serves the suggestion count and the grid's show-ignored view.
    if (includeIgnored) {
      return results;
    }
    return results.filter(result => !result.item.sideLoadedBusiness.isIgnored);
  };

export interface SideLoadedBusinessWithMatches {
  sideLoadedBusiness: SideLoadedBusiness;
  matchedFields: SideLoadedMatchedField[];
}

// PIC/ABN/email are identifiers, not names - a one-character difference is a different
// property/business, not a typo. Match these with a normalized substring check rather than
// Fuse's fuzzy (Levenshtein-style) matching, mirroring the same normalization the backend
// applies when storing these fields.
const normalizePicOrAbn = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

const normalizeDigits = (value: string): string => value.replace(/\D/g, "");

const normalizeLower = (value: string): string => value.trim().toLowerCase();

export function matchSideLoadedBusinessIdentifiers(
  businesses: SideLoadedBusiness[],
  query: string,
): SideLoadedBusinessWithMatches[] {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length < 2) {
    return EMPTY_ARRAY;
  }

  const picQuery = normalizePicOrAbn(trimmedQuery);
  const lowerQuery = normalizeLower(trimmedQuery);

  // Only treat the query as an ABN search when it's actually numeric - otherwise a
  // mostly-alpha query (e.g. "alai0") normalizes down to a trivial digit fragment that
  // would coincidentally substring-match almost any ABN.
  const isNumericQuery = /^\d+$/.test(trimmedQuery.replace(/\s/g, ""));
  const digitsQuery = isNumericQuery ? normalizeDigits(trimmedQuery) : "";

  return businesses.reduce(
    (matches: SideLoadedBusinessWithMatches[], business) => {
      const matchedFields: SideLoadedMatchedField[] = [];

      if (
        digitsQuery &&
        business.abn &&
        normalizeDigits(business.abn).includes(digitsQuery)
      ) {
        matchedFields.push("abn");
      }

      if (
        picQuery &&
        business.pics.split(PIC_DELIMITER).some(pic => pic.includes(picQuery))
      ) {
        matchedFields.push("pic");
      }

      if (
        business.brands &&
        normalizeLower(business.brands).includes(lowerQuery)
      ) {
        matchedFields.push("brands");
      }

      if (
        business.email &&
        normalizeLower(business.email).includes(lowerQuery)
      ) {
        matchedFields.push("email");
      }

      if (matchedFields.length) {
        matches.push({ sideLoadedBusiness: business, matchedFields });
      }
      return matches;
    },
    [],
  );
}

export const searchSideLoadedBusinessesWithMatches =
  (query: string, strict: boolean = false, includeIgnored: boolean = false) =>
  (state: State): SideLoadedBusinessWithMatches[] => {
    const businesses = getSideLoadedBusinesses(state);
    const identifierMatches = matchSideLoadedBusinessIdentifiers(
      Object.values(businesses),
      query,
    ).filter(match => includeIgnored || !match.sideLoadedBusiness.isIgnored);
    const identifierMatchIds = new Set(
      identifierMatches.map(match => match.sideLoadedBusiness.id),
    );

    const fuzzyMatches = searchSideLoadedBusinesses(
      query,
      strict,
      includeIgnored,
    )(state)
      .filter(
        result => !identifierMatchIds.has(result.item.sideLoadedBusiness.id),
      )
      .map(result => ({
        sideLoadedBusiness: result.item.sideLoadedBusiness,
        matchedFields: EMPTY_ARRAY,
      }));

    return identifierMatches.concat(fuzzyMatches);
  };

export const isHiddenSideLoadedBusiness = (
  business: Pick<SideLoadedBusiness, "isIgnored" | "businessIds">,
): boolean => business.isIgnored || business.businessIds.length > 0;

// The strict/liberal choice must be made on what's actually visible -
// otherwise a strict match that's only a currently-hidden record starves the
// liberal fallback of a less-exact, visible match it might have surfaced.
export function pickSideLoadedSearchResults(
  strictResults: SideLoadedBusinessWithMatches[],
  liberalResults: SideLoadedBusinessWithMatches[],
  showHidden: boolean,
): SideLoadedBusinessWithMatches[] {
  const visibleStrictResults = strictResults.filter(
    result =>
      showHidden || !isHiddenSideLoadedBusiness(result.sideLoadedBusiness),
  );
  return visibleStrictResults.length ? strictResults : liberalResults;
}

// Counts new suggestions only - used records are left in the search results
// for the grid, which labels them, but they are not work waiting to be done.
export const newSideLoadedBusinessSuggestionsCount =
  (query: string) =>
  (state: State): number =>
    searchSideLoadedBusinessesWithMatches(
      query,
      true,
    )(state).filter(result => !result.sideLoadedBusiness.businessIds.length)
      .length;

const NAME_AND_ABN_FIRST = ["name", "abn"];

// Not a comparator: this ordering isn't antisymmetric, so a sort would depend on
// the engine's internals.
const orderMappableFields = (columns: string[]): string[] => [
  ...NAME_AND_ABN_FIRST.filter(column => columns.includes(column)),
  ...columns.filter(column => !NAME_AND_ABN_FIRST.includes(column)),
];

export const selectSideLoadedBusinessMappableFieldsByIdLookup = createSelector(
  [getSideLoadedBusinesses, getSideLoadedDataSets],
  (
    sideLoadedBusinesses: Record<string, SideLoadedBusiness>,
    sideLoadedDataSets: Record<string, SideLoadedDataSet>,
  ) => {
    // Per data set, not per record: every record of one file has the same
    // columns, but two files do not.
    const columnsByDataSetId: Record<string, string[]> = {};

    Object.values(sideLoadedBusinesses).forEach(sideLoadedBusiness => {
      if (columnsByDataSetId[sideLoadedBusiness.datasetId]) {
        return;
      }
      // The data set's own columns are the file's true order; a record's keys
      // are the fallback until the data set has loaded.
      const sourceColumns =
        sideLoadedDataSets[sideLoadedBusiness.datasetId]?.sourceColumns;
      columnsByDataSetId[sideLoadedBusiness.datasetId] = orderMappableFields(
        sourceColumns?.length
          ? sourceColumns
          : Object.keys(sideLoadedBusiness.rawData),
      );
    });

    return Object.values(sideLoadedBusinesses).reduce<Record<string, string[]>>(
      (acc, sideLoadedBusiness) => {
        acc[sideLoadedBusiness.id] =
          columnsByDataSetId[sideLoadedBusiness.datasetId];
        return acc;
      },
      {},
    );
  },
);

export const getSideLoadedBusinessMappableFieldsById =
  (recordId: string) => (state: State) =>
    selectSideLoadedBusinessMappableFieldsByIdLookup(state)[recordId] ||
    EMPTY_ARRAY;

const selectSideLoadedBusinessMappableOptionsByIdLookup = createSelector(
  [getSideLoadedBusinesses, selectSideLoadedBusinessMappableFieldsByIdLookup],
  (sideLoadedBusinesses, mappableFieldsByIdLookup) => {
    return Object.values(sideLoadedBusinesses).reduce(
      (acc, sideLoadedBusiness: SideLoadedBusiness) => {
        const mappableFields = mappableFieldsByIdLookup[sideLoadedBusiness.id];
        // Show only mappable fields with data.
        acc[sideLoadedBusiness.id] = mappableFields
          .filter(field => Boolean(sideLoadedBusiness?.rawData[field]))
          .reduce((rowAcc, field) => {
            const fieldValue = sideLoadedBusiness?.rawData[field];

            rowAcc.push({
              label: fieldValue,
              value: field,
              disabled: !fieldValue,
            });

            return rowAcc;
          }, []);
        return acc;
      },
      {},
    );
  },
);

export const getSideLoadedBusinessMappableOptionsById =
  (recordId: string) => (state: State) => {
    return (
      selectSideLoadedBusinessMappableOptionsByIdLookup(state)[recordId] ||
      EMPTY_ARRAY
    );
  };
